import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, Tag, Button, Space, Modal, Form, Input, Select,
    Popconfirm, message, Typography, Empty, InputNumber,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InboxOutlined, RollbackOutlined } from '@ant-design/icons';
import { useFetchWrapper } from '_helpers';
import DatePicker from '_helpers/DatePicker';
import dayjs from '_helpers/datetime';
import { WEEKDAYS, formatSlots } from '_helpers/schedule';
import { MongoRef } from './MongoRef';

const { Text } = Typography;
const { Option } = Select;

export { ClassesTab };

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function ClassesTab() {
    const fetchWrapper = useFetchWrapper();
    const [rows, setRows]         = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading]   = useState(false);
    const [saving, setSaving]     = useState(false);

    // Edit modal (caretaker/book/dates/schedule — class_name is NOT editable here, see report).
    const [editRecord, setEditRecord]     = useState(null);
    const [scheduleSlots, setScheduleSlots] = useState([]); // [{weekday, start, duration_min}]
    const [form] = Form.useForm();

    // Assign-teacher modal
    const [assignRecord, setAssignRecord]   = useState(null);
    const [selectedTeacherId, setSelectedTeacherId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [classRes, teacherRes] = await Promise.all([
                fetchWrapper.get('/api/manager/classes', null, null),
                fetchWrapper.get('/api/manager/teachers', null, null),
            ]);
            const classJson   = await classRes.json();
            const teacherJson = await teacherRes.json();
            if (classJson.status === 'Success') setRows(Array.isArray(classJson.message) ? classJson.message : []);
            else message.error(classJson.message || 'Không tải được danh sách lớp');
            if (teacherJson.status === 'Success') setTeachers(Array.isArray(teacherJson.message) ? teacherJson.message : []);
        } catch (e) {
            message.error('Không tải được danh sách lớp');
        }
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    // ---- Archive / un-archive ----------------------------------------------------------
    async function handleArchive(record, archived) {
        try {
            const body = new URLSearchParams();
            body.append('archived', archived);
            const res  = await fetchWrapper.put(
                `/api/manager/classes/${record.class_id}/archive`,
                'application/x-www-form-urlencoded',
                body
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success(archived ? 'Đã lưu trữ lớp' : 'Đã khôi phục lớp');
                await load();
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
    }

    // ---- Delete class (cascades attendance/homework/tuition-link, irreversible) -------
    async function handleDelete(record) {
        try {
            const res  = await fetchWrapper.delete(`/api/manager/classes/${record.class_id}`, null, null);
            const json = await res.json();
            if (json.status === 'Success') { message.success('Đã xóa lớp'); await load(); }
            else message.error(typeof json.message === 'string' ? json.message : 'Xóa thất bại');
        } catch (e) { message.error('Xóa thất bại'); }
    }

    // ---- Assign / remove teacher --------------------------------------------------------
    function openAssign(record) {
        setSelectedTeacherId(null);
        setAssignRecord(record);
    }

    async function handleAssign() {
        if (!selectedTeacherId) { message.warning('Vui lòng chọn giáo viên'); return; }
        setSaving(true);
        try {
            const body = new URLSearchParams();
            body.append('teacher_student_id', selectedTeacherId);
            const res  = await fetchWrapper.post(
                `/api/manager/classes/${assignRecord.class_id}/assign`,
                'application/x-www-form-urlencoded',
                body
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success('Đã gán giáo viên vào lớp');
                setAssignRecord(null);
                await load();
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
        setSaving(false);
    }

    async function handleRemoveTeacher(record, teacherStudentId) {
        try {
            const res  = await fetchWrapper.delete(
                `/api/manager/classes/${record.class_id}/assign/${teacherStudentId}`,
                null, null
            );
            const json = await res.json();
            if (json.status === 'Success') { message.success('Đã xóa giáo viên khỏi lớp'); await load(); }
            else message.error(typeof json.message === 'string' ? json.message : 'Có lỗi xảy ra');
        } catch (e) { message.error('Có lỗi xảy ra'); }
    }

    // ---- Edit class info (caretaker / book / dates / schedule) -------------------------
    function openEdit(record) {
        form.setFieldsValue({
            caretaker:  record.caretaker || '',
            book:       record.book || '',
            start_date: record.start_date ? dayjs(record.start_date) : null,
            end_date:   record.end_date ? dayjs(record.end_date) : null,
        });
        setScheduleSlots((record.schedule_slots || []).map(s => ({
            weekday: s.weekday, start: s.start || '', duration_min: s.duration_min || 90,
        })));
        setEditRecord(record);
    }

    const setSlot = (i, patch) =>
        setScheduleSlots(slots => slots.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    const addSlot = () =>
        setScheduleSlots(slots => [...slots, { weekday: 1, start: '18:00', duration_min: 90 }]);
    const removeSlot = (i) =>
        setScheduleSlots(slots => slots.filter((_, idx) => idx !== i));

    async function handleSaveEdit() {
        let values;
        try { values = await form.validateFields(); } catch { return; }

        // Mirror server validation so the user gets a friendly message before the round-trip.
        for (const s of scheduleSlots) {
            if (!TIME_RE.test(s.start)) { message.warning('Giờ bắt đầu không hợp lệ (HH:MM)'); return; }
            if (!(s.duration_min >= 1 && s.duration_min <= 600)) { message.warning('Thời lượng 1–600 phút'); return; }
        }

        setSaving(true);
        try {
            const payload = {
                caretaker:      values.caretaker || '',
                book:           values.book || '',
                schedule_slots: scheduleSlots,
                start_date:     values.start_date ? values.start_date.valueOf() : null,
                end_date:       values.end_date ? values.end_date.valueOf() : null,
            };
            const res  = await fetchWrapper.put(
                `/api/manager/classes/${editRecord.class_id}/info`,
                'application/json',
                JSON.stringify(payload)
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success('Đã lưu thông tin lớp');
                setEditRecord(null);
                await load();
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
        setSaving(false);
    }

    // ---- Table columns -------------------------------------------------------------------
    const columns = [
        { title: 'Mongo', key: 'mongo', width: 120, render: (_, r) => <MongoRef id={r._id} collection="classes" /> },
        {
            title: 'Lớp',
            key: 'class_name',
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600 }}>
                        {r.class_name}
                        {r.is_trial && <Tag color="orange" style={{ marginLeft: 6 }}>Học thử</Tag>}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }} title={r.class_id}>
                        {r.class_id}
                    </Text>
                </div>
            ),
            sorter: (a, b) => (a.class_name || '').localeCompare(b.class_name || '', 'vi'),
        },
        {
            title: 'Giáo viên tạo lớp',
            key: 'class_teacher',
            render: (_, r) => r.class_teacher
                ? <span>{r.class_teacher.name} <Text type="secondary">({r.class_teacher.student_id})</Text></span>
                : <Text type="secondary">—</Text>,
        },
        {
            title: 'Giáo viên được gán',
            key: 'assigned_teachers',
            render: (_, r) => (
                <Space wrap size={4}>
                    {(r.assigned_teachers || []).length > 0
                        ? r.assigned_teachers.map(t => (
                            <Tag
                                key={t.student_id}
                                closable={!r.archived}
                                onClose={(e) => { e.preventDefault(); handleRemoveTeacher(r, t.student_id); }}
                            >
                                {t.name} ({t.student_id})
                            </Tag>
                        ))
                        : <Text type="secondary">Chưa có</Text>}
                </Space>
            ),
        },
        {
            title: 'Sĩ số',
            key: 'members',
            width: 80,
            align: 'center',
            sorter: (a, b) => (a.class_members?.length || 0) - (b.class_members?.length || 0),
            render: (_, r) => r.class_members?.length ?? 0,
        },
        {
            title: 'Lịch học',
            key: 'schedule',
            render: (_, r) => formatSlots(r.schedule_slots) || <Text type="secondary">Chưa có lịch</Text>,
        },
        {
            title: 'Trạng thái',
            key: 'archived',
            width: 110,
            filters: [{ text: 'Hoạt động', value: false }, { text: 'Lưu trữ', value: true }],
            onFilter: (v, r) => !!r.archived === v,
            render: (_, r) => r.archived
                ? <Tag color="default">Lưu trữ</Tag>
                : <Tag color="green">Hoạt động</Tag>,
        },
        {
            title: '',
            key: 'actions',
            width: 220,
            render: (_, r) => r.archived ? (
                <Space size={4}>
                    <Button size="small" icon={<RollbackOutlined />} onClick={() => handleArchive(r, false)}>
                        Bỏ lưu trữ
                    </Button>
                    <Popconfirm
                        title={`Xóa lớp "${r.class_name}"? Không thể hoàn tác.`}
                        onConfirm={() => handleDelete(r)}
                        okText="Xóa" cancelText="Hủy" okType="danger"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ) : (
                <Space size={4} wrap>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Sửa</Button>
                    <Button size="small" icon={<PlusOutlined />} onClick={() => openAssign(r)}>Gán GV</Button>
                    <Popconfirm
                        title={`Lưu trữ lớp "${r.class_name}"?`}
                        onConfirm={() => handleArchive(r, true)}
                        okText="Lưu trữ" cancelText="Hủy"
                    >
                        <Button size="small" icon={<InboxOutlined />} />
                    </Popconfirm>
                    <Popconfirm
                        title={`Xóa lớp "${r.class_name}"? Không thể hoàn tác.`}
                        onConfirm={() => handleDelete(r)}
                        okText="Xóa" cancelText="Hủy" okType="danger"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="att2-page soft-in">
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">Quản lý lớp học</h1>
                    <div className="att2-sub">{rows.length} lớp</div>
                </div>
            </div>

            <div className="float-panel soft-in-scale">
                <Table
                    columns={columns}
                    dataSource={rows.map(r => ({ ...r, key: r.class_id }))}
                    loading={loading}
                    scroll={{ x: 1100 }}
                    pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                    locale={{ emptyText: <Empty description="Chưa có lớp học" /> }}
                />
            </div>

            {/* Edit modal — caretaker / book / start-end date / weekly schedule.
                class_name is intentionally not editable: the server's /info endpoint
                (fUpdateClassInfo) does not accept a class_name field. */}
            <Modal
                title={`Sửa thông tin lớp: ${editRecord?.class_name || ''}`}
                visible={!!editRecord}
                onCancel={() => setEditRecord(null)}
                onOk={handleSaveEdit}
                confirmLoading={saving}
                okText="Lưu"
                cancelText="Hủy"
                width={640}
            >
                <Form form={form} layout="vertical">
                    <Space size={16} style={{ display: 'flex' }}>
                        <Form.Item name="caretaker" label="Người phụ trách" style={{ flex: 1 }}>
                            <Input placeholder="Tên người phụ trách" />
                        </Form.Item>
                        <Form.Item name="book" label="Giáo trình" style={{ flex: 1 }}>
                            <Input placeholder="Tên giáo trình" />
                        </Form.Item>
                    </Space>
                    <Space size={16} style={{ display: 'flex' }}>
                        <Form.Item name="start_date" label="Ngày bắt đầu" style={{ flex: 1 }}>
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Chọn ngày" />
                        </Form.Item>
                        <Form.Item name="end_date" label="Ngày kết thúc" style={{ flex: 1 }}>
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Chọn ngày" />
                        </Form.Item>
                    </Space>
                </Form>

                <div style={{ marginTop: 8 }}>
                    <Text strong>Lịch học hàng tuần</Text>
                    <p style={{ color: 'var(--ink-4, #888)', fontSize: 12, margin: '4px 0 8px' }}>
                        Mỗi buổi học lặp lại hàng tuần. Một lớp có thể có nhiều buổi.
                    </p>
                    {scheduleSlots.length === 0 && <p><Text type="secondary">Chưa có buổi nào.</Text></p>}
                    {scheduleSlots.map((s, i) => (
                        <Space key={i} align="center" style={{ marginBottom: 8, display: 'flex' }}>
                            <Select
                                style={{ width: 110 }}
                                value={s.weekday}
                                onChange={v => setSlot(i, { weekday: v })}
                            >
                                {WEEKDAYS.map(w => <Option key={w.iso} value={w.iso}>{w.label}</Option>)}
                            </Select>
                            <input
                                type="time"
                                value={s.start}
                                onChange={e => setSlot(i, { start: e.target.value })}
                            />
                            <InputNumber
                                min={1} max={600} step={15}
                                value={s.duration_min}
                                onChange={v => setSlot(i, { duration_min: v })}
                                style={{ width: 90 }}
                            />
                            <span>phút</span>
                            <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeSlot(i)} />
                        </Space>
                    ))}
                    <Button icon={<PlusOutlined />} onClick={addSlot}>Thêm buổi</Button>
                </div>
            </Modal>

            {/* Assign-teacher modal */}
            <Modal
                title={`Gán giáo viên vào lớp: ${assignRecord?.class_name || ''}`}
                visible={!!assignRecord}
                onCancel={() => setAssignRecord(null)}
                onOk={handleAssign}
                confirmLoading={saving}
                okText="Gán"
                cancelText="Hủy"
            >
                <p>Chọn giáo viên để gán vào lớp này:</p>
                <Select
                    style={{ width: '100%' }}
                    placeholder="Chọn giáo viên..."
                    value={selectedTeacherId}
                    onChange={setSelectedTeacherId}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        option.children.toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {teachers.map(t => (
                        <Option key={t.student_id} value={t.student_id}>
                            {t.name} ({t.student_id}) — {t.role}
                        </Option>
                    ))}
                </Select>
            </Modal>
        </div>
    );
}
